import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    Security as SecurityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import useModulePermissions from '../../hooks/useModulePermissions';
import ModuleAccessDialog from './ModuleAccessDialog';

interface User {
    _id: string;
    name: string;
    email: string;
}

interface UserPermissionsViewProps {
    user: User;
    onClose?: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`permission-tabpanel-${index}`}
            aria-labelledby={`permission-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const UserPermissionsView: React.FC<UserPermissionsViewProps> = ({ user, onClose }) => {
    const {
        userPermissions,
        availableModules,
        availableScopes,
        permissionHistory,
        loading,
        saving,
        error,
        loadUserPermissions,
        loadPermissionHistory,
        grantModuleAccess,
        revokeModuleAccess,
        updateContextPermissions,
        clearError
    } = useModulePermissions();

    const [tabValue, setTabValue] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [moduleToRevoke, setModuleToRevoke] = useState<string>('');

    useEffect(() => {
        if (user?._id) {
            loadUserPermissions(user._id);
        }
    }, [user?._id, loadUserPermissions]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        
        // Load history when switching to history tab
        if (newValue === 2 && user?._id && permissionHistory.length === 0) {
            loadPermissionHistory(user._id);
        }
    };

    const handleSavePermissions = async (data: any) => {
        if (data.type === 'module') {
            await grantModuleAccess(user._id, data.data, data.reason);
        } else if (data.type === 'context') {
            await updateContextPermissions(user._id, data.data, data.reason);
        }
    };

    const handleRevokeModule = (module: string) => {
        setModuleToRevoke(module);
        setRevokeDialogOpen(true);
    };

    const confirmRevokeModule = async () => {
        if (moduleToRevoke) {
            try {
                await revokeModuleAccess(user._id, moduleToRevoke, 'Manual revocation');
                setRevokeDialogOpen(false);
                setModuleToRevoke('');
            } catch (error) {
                // Error is handled by the hook
            }
        }
    };

    const getModuleName = (moduleId: string) => {
        const module = availableModules.find(m => m.id === moduleId);
        return module ? module.name : moduleId;
    };

    const getScopeName = (scopeId: string) => {
        const scope = availableScopes.find(s => s.id === scopeId);
        return scope ? scope.name : scopeId;
    };

    if (loading && !userPermissions) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h5" gutterBottom>
                    User Permissions - {user.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {user.email}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Module Access" icon={<SecurityIcon />} />
                    <Tab label="Manager Permissions" icon={<PersonIcon />} />
                    <Tab label="History" icon={<HistoryIcon />} />
                </Tabs>
            </Box>

            {/* Tab Panels */}
            <TabPanel value={tabValue} index={0}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Module Access Permissions</Typography>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setDialogOpen(true)}
                            disabled={saving}
                        >
                            Manage Access
                        </Button>
                    </Box>

                    <Grid container spacing={2}>
                        {userPermissions && Object.keys(userPermissions.moduleAccess).length > 0 ? (
                            Object.entries(userPermissions.moduleAccess).map(([module, access]: [string, any]) => (
                                <Grid item xs={12} sm={6} md={4} key={module}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {getModuleName(module)}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Scope: {getScopeName(access.scope)}
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRevokeModule(module)}
                                                    disabled={saving}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                            
                                            <Box sx={{ mt: 1 }}>
                                                {access.permissions.map((permission: string) => (
                                                    <Chip
                                                        key={permission}
                                                        label={permission}
                                                        size="small"
                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    No module access permissions assigned. Click "Manage Access" to add permissions.
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Manager & Approval Permissions</Typography>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setDialogOpen(true)}
                            disabled={saving}
                        >
                            Update Permissions
                        </Button>
                    </Box>

                    {userPermissions?.contextPermissions ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            General Manager Status
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.isManager ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Is Manager" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canAssignRoles ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Assign Roles" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            Approval Permissions
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveLeaves ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Leaves" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveAttendance ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Attendance" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveTimesheets ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Timesheets" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveExpenses ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Expenses" />
                                            </ListItem>
                                        </List>

                                        {userPermissions.contextPermissions.approvalScope && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Approval Scope: <strong>{userPermissions.contextPermissions.approvalScope}</strong>
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            No manager permissions assigned.
                        </Alert>
                    )}
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom>
                    Permission Change History
                </Typography>

                {permissionHistory.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Module</TableCell>
                                    <TableCell>Performed By</TableCell>
                                    <TableCell>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {permissionHistory.map((entry) => (
                                    <TableRow key={entry._id}>
                                        <TableCell>
                                            {format(new Date(entry.performedAt), 'MMM dd, yyyy HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={entry.action}
                                                size="small"
                                                color={
                                                    entry.action === 'granted' ? 'success' :
                                                    entry.action === 'revoked' ? 'error' :
                                                    'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{entry.changeType}</TableCell>
                                        <TableCell>{entry.module || '-'}</TableCell>
                                        <TableCell>{entry.performedBy.name}</TableCell>
                                        <TableCell>
                                            <Tooltip title={entry.reason || 'No reason provided'}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                    {entry.reason || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">
                        No permission changes recorded.
                    </Alert>
                )}
            </TabPanel>

            {/* Module Access Dialog */}
            <ModuleAccessDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                selectedUser={user}
                onSave={handleSavePermissions}
                availableModules={availableModules}
                availableScopes={availableScopes}
            />

            {/* Revoke Confirmation Dialog */}
            <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
                <DialogTitle>Revoke Module Access</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to revoke access to the <strong>{getModuleName(moduleToRevoke)}</strong> module?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRevokeModule}
                        color="error"
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Revoking...' : 'Revoke Access'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserPermissionsView;